import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Select from "react-select";
import dayjs from "dayjs";
import { MdClose, MdOutlineSearch } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { GET_OFFICE_ACTIVITY } from "./graphql";

// Who in the office changed what from the admin panel: cancellations,
// booking edits, wallet credits, payments, deletions, access changes.

const PAGE_SIZE = 50;

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "custom", label: "Custom" },
];

const CATEGORIES = [
  { key: "BOOKING", label: "Bookings", cls: "bg-sky-50 text-sky-700" },
  { key: "MONEY", label: "Money", cls: "bg-emerald-50 text-emerald-700" },
  { key: "PEOPLE", label: "People & leave", cls: "bg-violet-50 text-violet-700" },
  { key: "INVENTORY", label: "Inventory", cls: "bg-amber-50 text-amber-700" },
  { key: "MARKETING", label: "Marketing", cls: "bg-pink-50 text-pink-700" },
  { key: "CATALOG", label: "Services & app", cls: "bg-gray-100 text-gray-700" },
  { key: "ACCESS", label: "Access & settings", cls: "bg-red-50 text-red-700" },
  { key: "OTHER", label: "Other", cls: "bg-gray-100 text-gray-600" },
];
const CAT = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

// Plain words for the actions that matter most; the rest are split from
// their code name ("updateEmployeeActive" -> "Update employee active").
const ACTION_LABELS = {
  cancelAdminBooking: "Cancelled a booking",
  editBooking: "Edited a booking",
  reassignBooking: "Reassigned a booking",
  deleteBookings: "Deleted bookings",
  verifyBooking: "Verified a booking",
  acknowledgeBooking: "Acknowledged a booking",
  adminCreditWallet: "Credited a customer's wallet",
  adminDebitWallet: "Debited a customer's wallet",
  recordManualOnlinePayment: "Marked a booking paid online (manual)",
  settlePayments: "Settled a professional's cash",
  generateAdminPaymentLink: "Sent a payment link",
  cancelAdminPaymentLink: "Cancelled a payment link",
  resolveUnlinkedRazorpayPayment: "Linked a Razorpay payment",
  addPromocode: "Created a promo code",
  addUserToPromotion: "Gave a customer a promotion",
  blockUser: "Blocked / unblocked a customer",
  updateEmployeeActive: "Turned a professional on / off",
  updateLeaveStatus: "Decided a leave request",
  adminMarkAwaitingResponse: "Put a booking on hold",
  adminMarkCustomerResponded: "Released a booking from hold",
  updateOrderCallingStatus: "Logged a customer call",
  adminSetBookingRescheduleAllowed: "Allowed customer reschedule",
  addAdmin: "Added an admin",
  deleteAdmin: "Removed an admin",
  updateSystemConfig: "Changed a system setting",
  markPayoutPaid: "Marked a payout paid",
  addEmployeeAdjustment: "Added a payroll adjustment",
};
const actionLabel = (a) =>
  ACTION_LABELS[a] ||
  a.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase()).replace(/ ([A-Z])/g, (m) => m.toLowerCase());

const rupees = (v) => (v === null || v === undefined ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);
const when = (iso) => dayjs(iso).format("D MMM, h:mm A");

const Card = ({ title, hint, right, children }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
    {(title || right) && (
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, sub, valueClass = "text-gray-900" }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Toggle = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
  >
    {children}
  </button>
);

// The arguments the action was called with, as readable key/value lines.
const ArgsView = ({ args }) => {
  let parsed = null;
  try {
    parsed = args ? JSON.parse(args) : null;
  } catch (e) {
    return <pre className="text-xs whitespace-pre-wrap">{args}</pre>;
  }
  if (!parsed || !Object.keys(parsed).length) return <p className="text-xs text-gray-400">No details.</p>;
  const flat = [];
  const walk = (obj, prefix) =>
    Object.entries(obj).forEach(([k, v]) => {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) walk(v, key);
      else flat.push([key.replace(/^data\./, ""), Array.isArray(v) ? JSON.stringify(v) : String(v)]);
    });
  walk(parsed, "");
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
      {flat.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-gray-500">{k}</dt>
          <dd className="text-gray-800 break-all">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 38, borderColor: "#e5e7eb", boxShadow: "none" }),
  multiValue: (b) => ({ ...b, backgroundColor: "#FBB033" }),
};

const OfficeActivity = () => {
  const [preset, setPreset] = useState("last7");
  const [custom, setCustom] = useState({ start: dayjs().subtract(6, "day").format("YYYY-MM-DD"), end: dayjs().format("YYYY-MM-DD") });
  const [actorIds, setActorIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [flags, setFlags] = useState({ onlySensitive: false, onlyErrors: false, onlyAfterHours: false });
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(() => setPage(0), [preset, custom, actorIds, categories, flags, search]);

  const range = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    if (preset === "today") return { startDate: today, endDate: today };
    if (preset === "yesterday") {
      const y = dayjs().subtract(1, "day").format("YYYY-MM-DD");
      return { startDate: y, endDate: y };
    }
    if (preset === "last30") return { startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "custom") return { startDate: custom.start, endDate: custom.end };
    return { startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), endDate: today };
  }, [preset, custom]);

  const { data, loading, error } = useQuery(GET_OFFICE_ACTIVITY, {
    variables: {
      filter: { ...range, actorIds, categories, search, ...flags },
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });
  const res = data?.getOfficeActivity;
  const d = res?.data;
  const errorMessage = error?.message || (res?.status === "ERROR" && res.message);

  // The picker lists everyone active in the period; keep picked people in
  // it even if a filter hides them.
  const [knownPeople, setKnownPeople] = useState([]);
  useEffect(() => {
    if (!d?.people) return;
    setKnownPeople((prev) => {
      const byId = new Map(prev.map((p) => [p.value, p]));
      d.people.forEach((p) => p.actorId && byId.set(String(p.actorId), { value: String(p.actorId), label: p.actorName || "Unknown" }));
      return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
    });
  }, [d]);

  const totals = useMemo(() => {
    const t = { actions: 0, sensitive: 0, afterHours: 0, errors: 0, cancellations: 0, walletCredited: 0, manualPayments: 0 };
    (d?.people || []).forEach((p) => Object.keys(t).forEach((k) => (t[k] += p[k] || 0)));
    return t;
  }, [d]);

  const toggleCat = (k) => setCategories((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const toggleFlag = (k) => setFlags((f) => ({ ...f, [k]: !f[k] }));
  const pages = d ? Math.ceil(d.total / PAGE_SIZE) : 0;
  const hours = d?.officeHours;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div>
            <h1 className="text-2xl font-semibold text-white">Office activity</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Every change made from the admin panel, with who made it. Recording started when this page went live; older changes aren't here.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input type="date" value={custom.start} max={custom.end} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
                to
                <input type="date" value={custom.end} min={custom.start} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
              </div>
            )}
            <div className="min-w-[220px] flex-1 max-w-sm">
              <Select
                isMulti
                placeholder="Everyone in the office"
                options={knownPeople}
                value={knownPeople.filter((o) => actorIds.includes(o.value))}
                onChange={(sel) => setActorIds(sel ? sel.map((x) => x.value) : [])}
                styles={selectStyles}
              />
            </div>
            <span className="text-xs text-gray-400 ml-auto">
              {dayjs(range.startDate).format("D MMM YYYY")} – {dayjs(range.endDate).format("D MMM YYYY")}
            </span>
          </div>

          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load office activity: {errorMessage}</div>}
          {loading && !d && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {d && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi label="Changes" value={totals.actions} sub={`by ${d.people.length} ${d.people.length === 1 ? "person" : "people"}`} />
                <Kpi label="Sensitive" value={totals.sensitive} valueClass={totals.sensitive ? "text-amber-600" : "text-gray-900"} sub="money, cancellations, deletions, access" />
                <Kpi label="Bookings cancelled" value={totals.cancellations} sub="by the office" />
                <Kpi label="Wallet credited" value={rupees(totals.walletCredited)} valueClass={totals.walletCredited ? "text-amber-600" : "text-gray-900"} sub="free money given to customers" />
                <Kpi label="Marked paid manually" value={rupees(totals.manualPayments)} sub="online payments attached by hand" />
                <Kpi
                  label="Outside office hours"
                  value={totals.afterHours}
                  valueClass={totals.afterHours ? "text-red-600" : "text-gray-900"}
                  sub={hours ? `before ${hours.start}:00 or after ${hours.end}:00 IST` : ""}
                />
              </div>

              <Card title="By person" hint="Click a name to see only their changes.">
                <div className="overflow-x-auto -mx-4">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        {["Person", "Changes", "Sensitive", "Cancelled", "Edited", "Reassigned", "Wallet +", "Wallet −", "Manual paid", "Failed", "After hours", "Days active", "Last change"].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.people.map((p) => (
                        <tr key={p.actorId || "none"} className="border-b border-gray-50 hover:bg-gray-50 tabular-nums">
                          <td className="px-3 pl-4 py-2 whitespace-nowrap">
                            <button type="button" onClick={() => p.actorId && setActorIds([String(p.actorId)])} className="font-medium text-gray-900 hover:underline">
                              {p.actorName || "Unknown"}
                            </button>
                          </td>
                          <td className="px-3 py-2">{p.actions}</td>
                          <td className={`px-3 py-2 ${p.sensitive ? "text-amber-600 font-medium" : "text-gray-400"}`}>{p.sensitive}</td>
                          <td className="px-3 py-2">{p.cancellations || <span className="text-gray-300">0</span>}</td>
                          <td className="px-3 py-2">{p.bookingEdits || <span className="text-gray-300">0</span>}</td>
                          <td className="px-3 py-2">{p.reassignments || <span className="text-gray-300">0</span>}</td>
                          <td className={`px-3 py-2 ${p.walletCredited ? "text-amber-600" : "text-gray-300"}`}>{rupees(p.walletCredited)}</td>
                          <td className="px-3 py-2 text-gray-600">{rupees(p.walletDebited)}</td>
                          <td className="px-3 py-2 text-gray-600">{rupees(p.manualPayments)}</td>
                          <td className={`px-3 py-2 ${p.errors ? "text-red-600" : "text-gray-300"}`}>{p.errors}</td>
                          <td className={`px-3 py-2 ${p.afterHours ? "text-red-600 font-medium" : "text-gray-300"}`}>{p.afterHours}</td>
                          <td className="px-3 py-2">{p.activeDays}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">{p.lastAt ? when(p.lastAt) : "—"}</td>
                        </tr>
                      ))}
                      {!d.people.length && (
                        <tr>
                          <td colSpan={13} className="px-4 py-8 text-center text-gray-400">No changes in this period.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card
                title="Every change"
                hint={`${d.total} matching, newest first. Click a row for the details.`}
                right={
                  <div className="relative">
                    <MdOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Booking id, name, action…"
                      className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-64"
                    />
                  </div>
                }
              >
                <div className="flex flex-wrap gap-2 mb-3">
                  {CATEGORIES.map((c) => {
                    const n = d.categories.find((x) => x.category === c.key)?.actions || 0;
                    if (!n && !categories.includes(c.key)) return null;
                    return (
                      <Toggle key={c.key} on={categories.includes(c.key)} onClick={() => toggleCat(c.key)}>
                        {c.label} · {n}
                      </Toggle>
                    );
                  })}
                  <span className="w-px bg-gray-200 mx-1" />
                  <Toggle on={flags.onlySensitive} onClick={() => toggleFlag("onlySensitive")}>Sensitive only</Toggle>
                  <Toggle on={flags.onlyAfterHours} onClick={() => toggleFlag("onlyAfterHours")}>Outside office hours</Toggle>
                  <Toggle on={flags.onlyErrors} onClick={() => toggleFlag("onlyErrors")}>Failed</Toggle>
                  {(actorIds.length > 0 || categories.length > 0 || search || Object.values(flags).some(Boolean)) && (
                    <button
                      type="button"
                      onClick={() => {
                        setActorIds([]);
                        setCategories([]);
                        setFlags({ onlySensitive: false, onlyErrors: false, onlyAfterHours: false });
                        setSearchText("");
                      }}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 ml-1"
                    >
                      <MdClose /> Clear
                    </button>
                  )}
                </div>

                <div className="divide-y divide-gray-100 -mx-4">
                  {d.entries.map((e) => {
                    const cat = CAT[e.category] || CAT.OTHER;
                    const isOpen = open === e.id;
                    return (
                      <div key={e.id} className={`${e.sensitive ? "bg-amber-50/40" : ""}`}>
                        <button type="button" onClick={() => setOpen(isOpen ? null : e.id)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                          <span className="w-32 shrink-0 text-gray-500 tabular-nums">{when(e.createdAt)}</span>
                          <span className="w-32 shrink-0 font-medium text-gray-900 truncate">{e.actorName || (e.actorType === "EMPLOYEE" ? "Professional" : "Unknown")}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[11px] ${cat.cls}`}>{cat.label}</span>
                          <span className="text-gray-800">{actionLabel(e.action)}</span>
                          {e.bookingId && <span className="font-mono text-xs text-gray-600">{e.bookingId}</span>}
                          {e.amount !== null && <span className="font-semibold text-gray-900">{rupees(e.amount)}</span>}
                          <span className="ml-auto flex items-center gap-2">
                            {e.afterHours && <span className="px-1.5 py-0.5 rounded text-[11px] bg-red-50 text-red-700">After hours</span>}
                            {e.outcome === "ERROR" && <span className="px-1.5 py-0.5 rounded text-[11px] bg-red-50 text-red-700">Failed</span>}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-3 pl-[9.5rem] space-y-2">
                            {e.message && <p className={`text-xs ${e.outcome === "ERROR" ? "text-red-700" : "text-gray-600"}`}>{e.message}</p>}
                            <ArgsView args={e.args} />
                            <p className="text-[11px] text-gray-400">
                              {e.action}
                              {e.targetType && ` · ${e.targetType} ${e.targetId}`}
                              {e.ip && ` · IP ${e.ip}`}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!d.entries.length && <p className="px-4 py-8 text-center text-gray-400 text-sm">Nothing matches these filters.</p>}
                </div>

                {pages > 1 && (
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">
                      Newer
                    </button>
                    <span className="text-gray-500">
                      Page {page + 1} of {pages}
                    </span>
                    <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">
                      Older
                    </button>
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default OfficeActivity;
